import React, { useEffect, useState } from "react";
import { useAuth } from "../components/AuthContext";

const GRADE_COLORS = {
  A: { bg: '#d1fae5', color: '#065f46' },
  B: { bg: '#dbeafe', color: '#1e40af' },
  C: { bg: '#fef3c7', color: '#92400e' },
  D: { bg: '#fee2e2', color: '#dc2626' }
};

const PRIORITY_COLORS = {
  high: { bg: '#fee2e2', color: '#dc2626', border: '#fecaca' },
  medium: { bg: '#fef3c7', color: '#92400e', border: '#fde68a' },
  low: { bg: '#d1fae5', color: '#065f46', border: '#a7f3d0' }
};

const RISK_COLORS = {
  critical: { bg: '#fee2e2', color: '#dc2626', border: '#fecaca' },
  high: { bg: '#ffedd5', color: '#c2410c', border: '#fed7aa' },
  medium: { bg: '#fef3c7', color: '#92400e', border: '#fde68a' },
  low: { bg: '#d1fae5', color: '#065f46', border: '#a7f3d0' }
};

function formatCurrency(val) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val || 0);
}

function formatDate(d) {
  if (!d) return 'N/A';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function StatCard({ title, value, subtext, color }) {
  return (
    <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20 }}>
      <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 4 }}>{title}</div>
      <div style={{ fontSize: 28, fontWeight: 900, color: color || '#111827' }}>{value}</div>
      {subtext && <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>{subtext}</div>}
    </div>
  );
}

function ChangeIndicator({ value, label }) {
  const num = parseFloat(value) || 0;
  const isUp = num > 0;
  const isFlat = num === 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      <span style={{ color: isFlat ? '#6b7280' : isUp ? '#10b981' : '#ef4444', fontWeight: 700 }}>
        {isFlat ? '—' : isUp ? '▲' : '▼'} {Math.abs(num)}%
      </span>
      {label && <span style={{ fontSize: 12, color: '#9ca3af' }}>{label}</span>}
    </div>
  );
}

export default function AnalyticsDashboard() {
  const { adminEmail } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [insights, setInsights] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [lanes, setLanes] = useState([]);
  const [customerScores, setCustomerScores] = useState([]);
  const [carrierScores, setCarrierScores] = useState([]);
  const [loadingInsights, setLoadingInsights] = useState(false);
  const [rateForecast, setRateForecast] = useState(null);
  const [demandForecast, setDemandForecast] = useState(null);
  const [churnData, setChurnData] = useState(null);
  const [weeklyData, setWeeklyData] = useState(null);

  const headers = { 'x-admin-email': adminEmail, 'x-user-role': 'BROKER_ADMIN' };

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    setLoading(true);
    try {
      const [summaryRes, customersRes, carriersRes, lanesRes] = await Promise.all([
        fetch('/api/analytics/summary', { headers }).then(r => r.json()),
        fetch('/api/analytics/profitability/customers', { headers }).then(r => r.json()),
        fetch('/api/analytics/profitability/carriers', { headers }).then(r => r.json()),
        fetch('/api/analytics/profitability/lanes', { headers }).then(r => r.json())
      ]);

      setSummary(summaryRes.summary);
      setCustomers(customersRes.customers || []);
      setCarriers(carriersRes.carriers || []);
      setLanes(lanesRes.lanes || []);
    } catch (e) {
      console.error('Load analytics error:', e);
    } finally {
      setLoading(false);
    }
  }

  async function loadInsights() {
    setLoadingInsights(true);
    try {
      const res = await fetch('/api/analytics/insights', { headers });
      const data = await res.json();
      setInsights(data.insights || []);
    } catch (e) {
      console.error('Load insights error:', e);
    } finally {
      setLoadingInsights(false);
    }
  }

  async function loadScores() {
    try {
      const [custRes, carrRes] = await Promise.all([
        fetch('/api/analytics/scores/customers', { headers }).then(r => r.json()),
        fetch('/api/analytics/scores/carriers', { headers }).then(r => r.json())
      ]);
      setCustomerScores(custRes.customers || []);
      setCarrierScores(carrRes.carriers || []);
    } catch (e) {
      console.error('Load scores error:', e);
    }
  }

  async function loadForecasting() {
    try {
      const [rateRes, demandRes] = await Promise.all([
        fetch('/api/analytics/forecasting/rates', { headers }).then(r => r.json()),
        fetch('/api/analytics/forecasting/demand', { headers }).then(r => r.json())
      ]);
      setRateForecast(rateRes);
      setDemandForecast(demandRes);
    } catch (e) {
      console.error('Load forecasting error:', e);
    }
  }

  async function loadChurnRisk() {
    try {
      const res = await fetch('/api/analytics/churn-risk', { headers });
      const data = await res.json();
      setChurnData(data);
    } catch (e) {
      console.error('Load churn risk error:', e);
    }
  }

  async function loadWeekly() {
    try {
      const res = await fetch('/api/analytics/performance/weekly', { headers });
      const data = await res.json();
      setWeeklyData(data);
    } catch (e) {
      console.error('Load weekly error:', e);
    }
  }

  useEffect(() => {
    if (activeTab === 'insights' && insights.length === 0) loadInsights();
    if (activeTab === 'scores' && customerScores.length === 0) loadScores();
    if (activeTab === 'forecasting' && !rateForecast) loadForecasting();
    if (activeTab === 'churn' && !churnData) loadChurnRisk();
    if (activeTab === 'weekly' && !weeklyData) loadWeekly();
  }, [activeTab]);

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center' }}>Loading analytics...</div>;
  }

  const tabs = [
    { id: 'overview', label: '📊 Overview' },
    { id: 'insights', label: '💡 AI Insights' },
    { id: 'customers', label: '🏭 Customers' },
    { id: 'carriers', label: '🚛 Carriers' },
    { id: 'lanes', label: '🛣️ Lanes' },
    { id: 'scores', label: '⭐ Scores' },
    { id: 'forecasting', label: '🔮 Forecasting' },
    { id: 'churn', label: '⚠️ Churn Risk' },
    { id: 'weekly', label: '📅 Weekly' }
  ];

  return (
    <div style={{ maxWidth: 1400, margin: '0 auto', padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: '0 0 8px 0', fontSize: 28, fontWeight: 900 }}>🧠 AI Analytics</h1>
          <p style={{ margin: 0, color: '#6b7280' }}>Profitability insights powered by AI</p>
        </div>
        <button
          onClick={loadData}
          style={{ padding: '8px 16px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: 6, fontWeight: 600, cursor: 'pointer' }}
        >
          🔄 Refresh
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24, borderBottom: '2px solid #e5e7eb', paddingBottom: 8, flexWrap: 'wrap' }}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '10px 16px',
              background: activeTab === tab.id ? '#dc2626' : 'transparent',
              color: activeTab === tab.id ? 'white' : '#6b7280',
              border: 'none',
              borderRadius: 8,
              fontWeight: 700,
              cursor: 'pointer',
              fontSize: 13
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ================================================================ */}
      {/* OVERVIEW TAB                                                     */}
      {/* ================================================================ */}
      {activeTab === 'overview' && summary && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 16, marginBottom: 24 }}>
            <StatCard title="Total Loads" value={summary.totalLoads} subtext={`${summary.deliveryRate}% delivered`} />
            <StatCard title="Total Revenue" value={formatCurrency(summary.totalRevenue)} color="#10b981" />
            <StatCard title="Total Cost" value={formatCurrency(summary.totalCost)} color="#ef4444" />
            <StatCard title="Gross Profit" value={formatCurrency(summary.totalProfit)} subtext={`${summary.marginPercent}% margin`} color="#3b82f6" />
            <StatCard title="Avg Profit/Load" value={formatCurrency(summary.avgProfitPerLoad)} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
            <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20 }}>
              <h3 style={{ margin: '0 0 16px 0' }}>🏆 Top Customers by Profit</h3>
              {customers.slice(0, 5).map((c, i) => (
                <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: i < 4 ? '1px solid #f3f4f6' : 'none' }}>
                  <div>
                    <div style={{ fontWeight: 600 }}>{c.name}</div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>{c.loadCount} loads • {c.marginPercent}% margin</div>
                  </div>
                  <div style={{ fontWeight: 700, color: '#10b981' }}>{formatCurrency(c.totalProfit)}</div>
                </div>
              ))}
            </div>

            <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20 }}>
              <h3 style={{ margin: '0 0 16px 0' }}>🚛 Top Carriers by Margin</h3>
              {carriers.slice(0, 5).map((c, i) => (
                <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: i < 4 ? '1px solid #f3f4f6' : 'none' }}>
                  <div>
                    <div style={{ fontWeight: 600 }}>{c.name}</div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>{c.loadCount} loads • {c.completionRate}% completion</div>
                  </div>
                  <div style={{ fontWeight: 700, color: '#3b82f6' }}>{c.marginPercent}% margin</div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ marginTop: 24, background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20 }}>
            <h3 style={{ margin: '0 0 16px 0' }}>🛣️ Most Profitable Lanes</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', gap: 12, padding: '8px 0', borderBottom: '2px solid #e5e7eb', fontSize: 12, fontWeight: 700, color: '#6b7280' }}>
              <div>Lane</div><div>Loads</div><div>Avg Revenue</div><div>Margin</div><div>Total Profit</div>
            </div>
            {lanes.slice(0, 5).map((lane, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', gap: 12, padding: '12px 0', borderBottom: '1px solid #f3f4f6', alignItems: 'center' }}>
                <div style={{ fontWeight: 600 }}>{lane.origin} → {lane.destination}</div>
                <div>{lane.loadCount}</div>
                <div>{formatCurrency(lane.avgCustomerRate)}</div>
                <div style={{ color: parseFloat(lane.marginPercent) > 15 ? '#10b981' : '#f59e0b' }}>{lane.marginPercent}%</div>
                <div style={{ fontWeight: 700, color: '#10b981' }}>{formatCurrency(lane.totalProfit)}</div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ================================================================ */}
      {/* AI INSIGHTS TAB                                                  */}
      {/* ================================================================ */}
      {activeTab === 'insights' && (
        <div>
          {loadingInsights ? (
            <div style={{ padding: 60, textAlign: 'center', background: 'white', borderRadius: 12, border: '1px solid #e5e7eb' }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>🧠</div>
              <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>Analyzing your data...</div>
              <div style={{ color: '#6b7280' }}>AI is generating personalized insights</div>
            </div>
          ) : insights.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', background: 'white', borderRadius: 12, border: '1px solid #e5e7eb' }}>
              <button onClick={loadInsights} style={{ padding: '12px 24px', background: '#dc2626', color: 'white', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 16 }}>
                Generate AI Insights
              </button>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 16 }}>
              {insights.map((insight, i) => {
                const priority = PRIORITY_COLORS[insight.priority] || PRIORITY_COLORS.medium;
                return (
                  <div key={i} style={{ background: 'white', border: `1px solid ${priority.border}`, borderLeft: `4px solid ${priority.color}`, borderRadius: 12, padding: 20 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
                      <div style={{ fontSize: 32 }}>{insight.icon}</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                          <div style={{ fontWeight: 700, fontSize: 16 }}>{insight.title}</div>
                          <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: priority.bg, color: priority.color }}>
                            {insight.priority.toUpperCase()}
                          </span>
                        </div>
                        <div style={{ color: '#374151', marginBottom: 12 }}>{insight.insight}</div>
                        <div style={{ padding: 12, background: '#f9fafb', borderRadius: 8, fontSize: 14 }}>
                          <strong>💡 Recommended Action:</strong> {insight.action}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
              <button onClick={loadInsights} style={{ padding: '12px 24px', background: 'white', border: '2px solid #e5e7eb', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>
                🔄 Regenerate Insights
              </button>
            </div>
          )}
        </div>
      )}

      {/* ================================================================ */}
      {/* CUSTOMERS TAB                                                    */}
      {/* ================================================================ */}
      {activeTab === 'customers' && (
        <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr', gap: 12, padding: 16, background: '#f9fafb', fontWeight: 700, fontSize: 12, color: '#6b7280' }}>
            <div>Customer</div><div>Loads</div><div>Revenue</div><div>Cost</div><div>Profit</div><div>Margin</div>
          </div>
          {customers.map(c => (
            <div key={c.id} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr', gap: 12, padding: 16, borderTop: '1px solid #f3f4f6', alignItems: 'center' }}>
              <div>
                <div style={{ fontWeight: 600 }}>{c.name}</div>
                <div style={{ fontSize: 12, color: '#9ca3af' }}>{c.mcNumber || 'No MC#'}</div>
              </div>
              <div>{c.loadCount}</div>
              <div style={{ color: '#10b981' }}>{formatCurrency(c.totalRevenue)}</div>
              <div style={{ color: '#ef4444' }}>{formatCurrency(c.totalCost)}</div>
              <div style={{ fontWeight: 700, color: '#3b82f6' }}>{formatCurrency(c.totalProfit)}</div>
              <div>
                <span style={{
                  padding: '4px 8px', borderRadius: 4, fontSize: 12, fontWeight: 600,
                  background: parseFloat(c.marginPercent) > 20 ? '#d1fae5' : parseFloat(c.marginPercent) > 10 ? '#fef3c7' : '#fee2e2',
                  color: parseFloat(c.marginPercent) > 20 ? '#065f46' : parseFloat(c.marginPercent) > 10 ? '#92400e' : '#dc2626'
                }}>
                  {c.marginPercent}%
                </span>
              </div>
            </div>
          ))}
          {customers.length === 0 && <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>No customer data yet</div>}
        </div>
      )}

      {/* ================================================================ */}
      {/* CARRIERS TAB                                                     */}
      {/* ================================================================ */}
      {activeTab === 'carriers' && (
        <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr', gap: 12, padding: 16, background: '#f9fafb', fontWeight: 700, fontSize: 12, color: '#6b7280' }}>
            <div>Carrier</div><div>Loads</div><div>Paid</div><div>Avg Rate</div><div>Completion</div><div>Margin</div>
          </div>
          {carriers.map(c => (
            <div key={c.id} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr', gap: 12, padding: 16, borderTop: '1px solid #f3f4f6', alignItems: 'center' }}>
              <div>
                <div style={{ fontWeight: 600 }}>{c.name}</div>
                <div style={{ fontSize: 12, color: '#9ca3af' }}>MC# {c.mcNumber || 'N/A'}</div>
              </div>
              <div>{c.loadCount}</div>
              <div>{formatCurrency(c.totalPaid)}</div>
              <div>{formatCurrency(c.avgRate)}</div>
              <div>
                <span style={{
                  padding: '4px 8px', borderRadius: 4, fontSize: 12, fontWeight: 600,
                  background: parseFloat(c.completionRate) > 90 ? '#d1fae5' : '#fef3c7',
                  color: parseFloat(c.completionRate) > 90 ? '#065f46' : '#92400e'
                }}>
                  {c.completionRate}%
                </span>
              </div>
              <div style={{ fontWeight: 700, color: '#3b82f6' }}>{c.marginPercent}%</div>
            </div>
          ))}
          {carriers.length === 0 && <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>No carrier data yet</div>}
        </div>
      )}

      {/* ================================================================ */}
      {/* LANES TAB                                                        */}
      {/* ================================================================ */}
      {activeTab === 'lanes' && (
        <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr 1fr', gap: 12, padding: 16, background: '#f9fafb', fontWeight: 700, fontSize: 12, color: '#6b7280' }}>
            <div>Lane</div><div>Loads</div><div>Avg Miles</div><div>Revenue</div><div>$/Mile</div><div>Profit</div><div>Margin</div>
          </div>
          {lanes.map((lane, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr 1fr', gap: 12, padding: 16, borderTop: '1px solid #f3f4f6', alignItems: 'center' }}>
              <div style={{ fontWeight: 600 }}>{lane.origin} → {lane.destination}</div>
              <div>{lane.loadCount}</div>
              <div>{lane.avgMiles}</div>
              <div>{formatCurrency(lane.totalRevenue)}</div>
              <div>${lane.revenuePerMile}</div>
              <div style={{ fontWeight: 700, color: '#10b981' }}>{formatCurrency(lane.totalProfit)}</div>
              <div>
                <span style={{
                  padding: '4px 8px', borderRadius: 4, fontSize: 12, fontWeight: 600,
                  background: parseFloat(lane.marginPercent) > 20 ? '#d1fae5' : '#fef3c7',
                  color: parseFloat(lane.marginPercent) > 20 ? '#065f46' : '#92400e'
                }}>
                  {lane.marginPercent}%
                </span>
              </div>
            </div>
          ))}
          {lanes.length === 0 && <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>No lane data yet</div>}
        </div>
      )}

      {/* ================================================================ */}
      {/* SCORES TAB                                                       */}
      {/* ================================================================ */}
      {activeTab === 'scores' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
          <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20 }}>
            <h3 style={{ margin: '0 0 16px 0' }}>🏭 Customer Scores</h3>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 16 }}>
              Based on: Volume (30%) • Margin (40%) • Recency (30%)
            </div>
            {customerScores.slice(0, 10).map((c, i) => {
              const grade = GRADE_COLORS[c.grade] || GRADE_COLORS.C;
              return (
                <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: i < 9 ? '1px solid #f3f4f6' : 'none' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, background: grade.bg, color: grade.color }}>
                      {c.grade}
                    </span>
                    <div>
                      <div style={{ fontWeight: 600 }}>{c.name}</div>
                      <div style={{ fontSize: 12, color: '#6b7280' }}>{c.loadCount} loads • {c.marginPercent}% margin</div>
                    </div>
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 900, color: '#3b82f6' }}>{c.overallScore}</div>
                </div>
              );
            })}
            {customerScores.length === 0 && <div style={{ padding: 20, textAlign: 'center', color: '#9ca3af' }}>Loading scores...</div>}
          </div>

          <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20 }}>
            <h3 style={{ margin: '0 0 16px 0' }}>🚛 Carrier Scores</h3>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 16 }}>
              Based on: Reliability (50%) • Volume (30%) • Cost (20%)
            </div>
            {carrierScores.slice(0, 10).map((c, i) => {
              const grade = GRADE_COLORS[c.grade] || GRADE_COLORS.C;
              return (
                <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: i < 9 ? '1px solid #f3f4f6' : 'none' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, background: grade.bg, color: grade.color }}>
                      {c.grade}
                    </span>
                    <div>
                      <div style={{ fontWeight: 600 }}>{c.name}</div>
                      <div style={{ fontSize: 12, color: '#6b7280' }}>{c.loadCount} loads • {c.onTimePercent}% on-time</div>
                    </div>
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 900, color: '#3b82f6' }}>{c.overallScore}</div>
                </div>
              );
            })}
            {carrierScores.length === 0 && <div style={{ padding: 20, textAlign: 'center', color: '#9ca3af' }}>Loading scores...</div>}
          </div>
        </div>
      )}

      {/* ================================================================ */}
      {/* FORECASTING TAB                                                  */}
      {/* ================================================================ */}
      {activeTab === 'forecasting' && (
        <div>
          {!rateForecast ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading forecasting data...</div>
          ) : (
            <>
              {/* Forecast Cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
                <StatCard title="Avg Rate (Current)" value={formatCurrency(rateForecast.history?.slice(-1)[0]?.avgRate || 0)} color="#3b82f6" />
                <StatCard title="Forecast (1 Month)" value={formatCurrency(rateForecast.forecast?.nextMonth)} subtext="+2% projected" color="#10b981" />
                <StatCard title="Forecast (3 Months)" value={formatCurrency(rateForecast.forecast?.next3Months)} subtext="+5% projected" color="#f59e0b" />
                <StatCard title="Forecast (6 Months)" value={formatCurrency(rateForecast.forecast?.next6Months)} subtext={`+8% projected • ${rateForecast.forecast?.confidence} confidence`} color="#8b5cf6" />
              </div>

              {/* Rate History Table */}
              <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden', marginBottom: 24 }}>
                <div style={{ padding: 20, borderBottom: '1px solid #e5e7eb' }}>
                  <h3 style={{ margin: 0 }}>📈 Rate History (Monthly)</h3>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 1fr 1fr', gap: 12, padding: 16, background: '#f9fafb', fontWeight: 700, fontSize: 12, color: '#6b7280' }}>
                  <div>Month</div><div>Loads</div><div>Avg Rate</div><div>Min Rate</div><div>Max Rate</div><div>Avg Miles</div><div>$/Mile</div>
                </div>
                {(rateForecast.history || []).map((m, i) => (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 1fr 1fr', gap: 12, padding: 16, borderTop: '1px solid #f3f4f6', alignItems: 'center' }}>
                    <div style={{ fontWeight: 600 }}>{m.month}</div>
                    <div>{m.loadCount}</div>
                    <div>{formatCurrency(m.avgRate)}</div>
                    <div style={{ color: '#6b7280' }}>{formatCurrency(m.minRate)}</div>
                    <div style={{ color: '#6b7280' }}>{formatCurrency(m.maxRate)}</div>
                    <div>{m.avgMiles} mi</div>
                    <div style={{ fontWeight: 600 }}>${m.ratePerMile}</div>
                  </div>
                ))}
                {(!rateForecast.history || rateForecast.history.length === 0) && (
                  <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>No historical rate data yet</div>
                )}
              </div>

              {/* Demand Forecast */}
              {demandForecast && demandForecast.lanes && demandForecast.lanes.length > 0 && (
                <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
                  <div style={{ padding: 20, borderBottom: '1px solid #e5e7eb' }}>
                    <h3 style={{ margin: 0 }}>🗺️ Lane Demand Forecast</h3>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12, padding: 16, background: '#f9fafb', fontWeight: 700, fontSize: 12, color: '#6b7280' }}>
                    <div>Lane</div><div>Total Loads</div><div>Avg/Month</div><div>Trend</div>
                  </div>
                  {demandForecast.lanes.map((lane, i) => (
                    <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12, padding: 16, borderTop: '1px solid #f3f4f6', alignItems: 'center' }}>
                      <div style={{ fontWeight: 600 }}>{lane.lane}</div>
                      <div>{lane.totalLoads}</div>
                      <div>{lane.avgMonthlyLoads}</div>
                      <div>
                        <span style={{
                          padding: '4px 10px', borderRadius: 4, fontSize: 12, fontWeight: 700,
                          background: lane.trend === 'up' ? '#d1fae5' : lane.trend === 'down' ? '#fee2e2' : '#f3f4f6',
                          color: lane.trend === 'up' ? '#065f46' : lane.trend === 'down' ? '#dc2626' : '#6b7280'
                        }}>
                          {lane.trend === 'up' ? '📈 Growing' : lane.trend === 'down' ? '📉 Declining' : '➡️ Stable'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ================================================================ */}
      {/* CHURN RISK TAB                                                   */}
      {/* ================================================================ */}
      {activeTab === 'churn' && (
        <div>
          {!churnData ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading churn risk data...</div>
          ) : (
            <>
              {/* Summary Cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 24 }}>
                <StatCard title="Total Customers" value={churnData.summary?.total || 0} />
                <StatCard title="At Risk Customers" value={churnData.summary?.atRisk || 0} color="#dc2626" />
                <StatCard title="At Risk Revenue" value={formatCurrency(churnData.summary?.atRiskRevenue)} color="#f59e0b" subtext="Potential revenue loss" />
              </div>

              {/* Customer Churn Table */}
              <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr', gap: 12, padding: 16, background: '#f9fafb', fontWeight: 700, fontSize: 12, color: '#6b7280' }}>
                  <div>Customer</div><div>Total Loads</div><div>Revenue</div><div>Last Load</div><div>Days Inactive</div><div>Risk Level</div>
                </div>
                {(churnData.customers || []).map(c => {
                  const risk = RISK_COLORS[c.riskLevel] || RISK_COLORS.low;
                  return (
                    <div key={c.id} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr', gap: 12, padding: 16, borderTop: '1px solid #f3f4f6', alignItems: 'center' }}>
                      <div style={{ fontWeight: 600 }}>{c.name}</div>
                      <div>{c.totalLoads}</div>
                      <div>{formatCurrency(c.totalRevenue)}</div>
                      <div style={{ fontSize: 13, color: '#6b7280' }}>{formatDate(c.lastLoadDate)}</div>
                      <div style={{ fontWeight: 700, color: c.daysSinceLastLoad > 60 ? '#dc2626' : c.daysSinceLastLoad > 30 ? '#f59e0b' : '#6b7280' }}>
                        {c.daysSinceLastLoad} days
                      </div>
                      <div>
                        <span style={{
                          padding: '4px 10px', borderRadius: 4, fontSize: 12, fontWeight: 700,
                          background: risk.bg, color: risk.color
                        }}>
                          {c.riskLevel.toUpperCase()}
                        </span>
                      </div>
                    </div>
                  );
                })}
                {(!churnData.customers || churnData.customers.length === 0) && (
                  <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>No customer data yet</div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* ================================================================ */}
      {/* WEEKLY PERFORMANCE TAB                                           */}
      {/* ================================================================ */}
      {activeTab === 'weekly' && (
        <div>
          {!weeklyData ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading weekly data...</div>
          ) : (
            <>
              {/* WoW Change Cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
                <StatCard title="This Week Loads" value={weeklyData.current?.loadCount || 0} />
                <StatCard title="This Week Revenue" value={formatCurrency(weeklyData.current?.revenue)} color="#10b981" />
                <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20 }}>
                  <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 4 }}>Load Change (WoW)</div>
                  <ChangeIndicator value={weeklyData.changes?.loads} label="vs last week" />
                </div>
                <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20 }}>
                  <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 4 }}>Revenue Change (WoW)</div>
                  <ChangeIndicator value={weeklyData.changes?.revenue} label="vs last week" />
                </div>
              </div>

              {/* Weekly Table */}
              <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
                <div style={{ padding: 20, borderBottom: '1px solid #e5e7eb' }}>
                  <h3 style={{ margin: 0 }}>📅 Weekly Performance (Last 12 Weeks)</h3>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 1fr', gap: 12, padding: 16, background: '#f9fafb', fontWeight: 700, fontSize: 12, color: '#6b7280' }}>
                  <div>Week Starting</div><div>Loads</div><div>Revenue</div><div>Delivered</div><div>Cancelled</div><div>Success Rate</div>
                </div>
                {(weeklyData.weeks || []).map((w, i) => (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 1fr', gap: 12, padding: 16, borderTop: '1px solid #f3f4f6', alignItems: 'center' }}>
                    <div style={{ fontWeight: 600 }}>{formatDate(w.week)}</div>
                    <div>{w.loadCount}</div>
                    <div style={{ color: '#10b981' }}>{formatCurrency(w.revenue)}</div>
                    <div style={{ color: '#3b82f6' }}>{w.delivered}</div>
                    <div style={{ color: w.cancelled > 0 ? '#dc2626' : '#6b7280' }}>{w.cancelled}</div>
                    <div>
                      <span style={{
                        padding: '4px 8px', borderRadius: 4, fontSize: 12, fontWeight: 700,
                        background: parseFloat(w.successRate) > 90 ? '#d1fae5' : parseFloat(w.successRate) > 70 ? '#fef3c7' : '#fee2e2',
                        color: parseFloat(w.successRate) > 90 ? '#065f46' : parseFloat(w.successRate) > 70 ? '#92400e' : '#dc2626'
                      }}>
                        {w.successRate}%
                      </span>
                    </div>
                  </div>
                ))}
                {(!weeklyData.weeks || weeklyData.weeks.length === 0) && (
                  <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>No weekly data yet</div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
